import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { ChevronDown } from "untitledui-js-base";
import { cx } from "shared/utils";
import {
  Alert02Icon,
  InformationCircleIcon
} from "@hugeicons-pro/core-solid-rounded";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { Banner } from "shared/components/Banner";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { TextArea } from "shared/components/forms";
import { CharCounter } from "shared/components/CharCounter";
import { SectionCard } from "shared/app/SectionCard";
import { Row } from "views/applications-id-report/components/Row";
import { unitsApi, UNIT_INSTRUCTIONS_UPDATE_KEY } from "api/units";
import { useAuthContext } from "providers/Auth";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { getErrorMessage } from "utils/error";
import type { InstructionType, Unit } from "types/landlord/address";
import { ApplyUnitInstructionsModal } from "shared/app/ApplyUnitInstructions";

const MAX_LENGTH = 1000;

const LABELS: Record<InstructionType, string> = {
  entry: "Entry instructions",
  exit: "Exit instructions"
};

const EMPTY_TEMPLATE_HINTS: Record<InstructionType, string> = {
  entry: "Leads get the lockbox code only.",
  exit: "Leads get no exit instructions."
};

const formatDate = (value: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

interface InstructionsSectionProps {
  unit: Unit;
  type: InstructionType;
}

export const InstructionsSection = ({
  unit,
  type
}: InstructionsSectionProps) => {
  const queryClient = useQueryClient();
  const { permissions } = useAuthContext();
  const dictionary = useUnitsPropertiesDictionary();

  const instruction = unit.instructions?.[type];
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [showTemplate, setShowTemplate] = useState(false);
  const [confirmingRevert, setConfirmingRevert] = useState(false);
  const [applying, setApplying] = useState(false);

  const canEdit = permissions.canChangeUnit;

  const { mutate: save, isPending } = useMutation({
    mutationKey: [UNIT_INSTRUCTIONS_UPDATE_KEY, unit.id, type],
    mutationFn: (value: string) =>
      unitsApi.updateInstructions(unit.id, {
        [`${type}_instructions`]: value
      }),
    onSuccess: (updated, value) => {
      queryClient.invalidateQueries({
        queryKey: ["unit", String(unit.property.id), String(unit.id)]
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", String(unit.property.id)]
      });
      setIsEditing(false);

      if (value === "") {
        toast.success("Reverted to template instructions");
        return;
      }

      toast.success(
        updated.instructions?.[type]?.state === "custom"
          ? `${LABELS[type]} updated`
          : `This ${dictionary.unit} is using the template's ${LABELS[
              type
            ].toLowerCase()}`
      );
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  if (!instruction) return null;

  const isCustom = instruction.state === "custom";
  const templateHasText = Boolean(instruction.template_text?.trim());
  const trimmedDraft = draft.trim();
  const matchesTemplate =
    trimmedDraft !== "" &&
    trimmedDraft === (instruction.template_text ?? "").trim();

  const startEditing = () => {
    setDraft(instruction.unit_text ?? instruction.template_text ?? "");
    setIsEditing(true);
  };

  const templateLink = instruction.template_identifier ? (
    <Link
      to={`/listing-settings/${instruction.template_identifier}`}
      className="text-sm font-bold text-primary-500 hover:text-primary-600"
    >
      {instruction.template_name ?? "Listing template"}
    </Link>
  ) : (
    <span className="text-sm font-medium text-content-600">
      {instruction.template_name ?? "Listing template"}
    </span>
  );

  const templateDisclosure = templateHasText ? (
    <div>
      <button
        type="button"
        onClick={() => setShowTemplate((open) => !open)}
        className="flex items-center gap-1.5 text-sm font-medium text-content-600 hover:text-content-1000"
        aria-expanded={showTemplate}
      >
        <ChevronDown
          className={cx(
            "w-4 shrink-0 transition-transform",
            showTemplate ? "rotate-180" : "rotate-0"
          )}
        />
        {showTemplate
          ? "Hide template instructions"
          : "View template instructions"}
      </button>
      <Show if={showTemplate}>
        <p className="mt-2 whitespace-pre-wrap rounded-lg bg-background-200 px-2.5 py-1.5 text-base text-content-600">
          {instruction.template_text}
        </p>
      </Show>
    </div>
  ) : null;

  const headerActions = () => {
    if (!canEdit || isEditing) return null;

    if (isCustom)
      return (
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            onClick={() => setApplying(true)}
          >
            Apply to other {dictionary.units}
          </Button>
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            onClick={startEditing}
          >
            Edit
          </Button>
          <Button
            className="ml-2"
            size={ButtonSize.SM}
            variant={ButtonVariant.TEXT}
            onClick={() => setConfirmingRevert(true)}
          >
            Revert to template
          </Button>
        </div>
      );

    return (
      <Button
        size={ButtonSize.SM}
        variant={ButtonVariant.BASE}
        onClick={startEditing}
      >
        {templateHasText
          ? `Override for this ${dictionary.unit}`
          : `Add instructions for this ${dictionary.unit}`}
      </Button>
    );
  };

  const editedMeta = () => {
    const date = formatDate(instruction.updated_at);
    if (!date) return null;

    const appliedFrom = instruction.applied_from;
    if (!appliedFrom) return { title: "Last edited", value: date };

    const origin = [appliedFrom.unit_title, appliedFrom.property_name]
      .filter(Boolean)
      .join(", ");

    return {
      title: "Applied from",
      value: origin
        ? `${origin} on ${date}`
        : `Another ${dictionary.unit} on ${date}`
    };
  };

  const meta = editedMeta();

  return (
    <SectionCard title={LABELS[type]} headerExtra={headerActions()}>
      <Show if={isEditing}>
        <div className="space-y-4">
          <div className="relative">
            <TextArea
              id={`${type}-instructions-${unit.id}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={MAX_LENGTH}
              rows={5}
              autoExpand
              disabled={isPending}
            />
            <CharCounter current={draft.length} max={MAX_LENGTH} />
          </div>

          {templateDisclosure}

          <div className="flex items-center justify-end space-x-3">
            <Show if={trimmedDraft === ""}>
              <p className="mr-auto text-sm font-medium text-content-300">
                To use the template&apos;s instructions, choose Revert to
                template instead.
              </p>
            </Show>
            <Show if={matchesTemplate}>
              <p className="mr-auto text-sm font-medium text-content-300">
                Matches the template. Saving keeps this {dictionary.unit} on the
                template.
              </p>
            </Show>
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.BASE}
              onClick={() => setIsEditing(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              onClick={() => save(trimmedDraft)}
              disabled={trimmedDraft === "" || isPending}
            >
              Save
            </Button>
          </div>
        </div>
      </Show>

      <Show if={!isEditing}>
        <div className="space-y-4">
          <Show if={Boolean(isCustom && instruction.template_updated_at)}>
            <Banner
              variant="warning"
              textTheme="base"
              className="rounded-lg"
              body={`The template's instructions were updated on ${formatDate(
                instruction.template_updated_at
              )}. This ${dictionary.unit} is still using its own instructions.`}
              HugeIcon={
                <HugeiconsIcon
                  icon={Alert02Icon}
                  className="shrink-0 text-warning-500"
                  size="20"
                />
              }
            />
          </Show>

          <Row
            title="Source"
            growValueWidth
            value={
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Badge
                  size={BadgeSize.SM}
                  variant={isCustom ? BadgeVariant.PRIMARY : BadgeVariant.BASE}
                >
                  {isCustom
                    ? `Custom for this ${dictionary.unit}`
                    : "From listing template"}
                </Badge>
                {templateLink}
              </span>
            }
          />

          <Show if={isCustom}>
            <Row
              title={LABELS[type]}
              growValueWidth
              value={
                <span className="whitespace-pre-wrap">
                  {instruction.unit_text}
                </span>
              }
            />
            <Show if={Boolean(meta)}>
              <Row
                title={meta?.title ?? ""}
                growValueWidth
                value={meta?.value}
              />
            </Show>
            {templateDisclosure}
          </Show>

          <Show if={!isCustom && templateHasText}>
            <Row
              title={LABELS[type]}
              growValueWidth
              value={
                <span className="whitespace-pre-wrap text-content-600">
                  {instruction.template_text}
                </span>
              }
            />
            <div className="flex items-start space-x-2">
              <HugeiconsIcon
                icon={InformationCircleIcon}
                size={16}
                className="mt-0.5 shrink-0 text-alpha-100"
              />
              <p className="text-sm font-medium text-content-300">
                Editing this template&apos;s instructions will update this{" "}
                {dictionary.unit} automatically.
              </p>
            </div>
          </Show>

          <Show if={!isCustom && !templateHasText}>
            <Row
              title={LABELS[type]}
              growValueWidth
              value={
                <span className="text-content-600">
                  <span className="font-bold text-content-1000">
                    {instruction.template_name ?? "This template"}
                  </span>{" "}
                  has no {LABELS[type].toLowerCase()}.{" "}
                  {EMPTY_TEMPLATE_HINTS[type]}
                </span>
              }
            />
          </Show>
        </div>
      </Show>

      <ConfirmDialog
        opened={confirmingRevert}
        onClose={() => setConfirmingRevert(false)}
        onConfirm={() => {
          setConfirmingRevert(false);
          save("");
        }}
        title="Revert to template instructions?"
        description={
          <>
            This {dictionary.unit}&apos;s custom {LABELS[type].toLowerCase()}{" "}
            will be deleted. Leads will get the instructions from{" "}
            <span className="font-bold text-content-1000">
              {instruction.template_name ?? "the listing template"}
            </span>
            .
          </>
        }
        variant="danger"
        icon={{ icon: Alert02Icon, variant: "danger" }}
        confirmButton={{ label: "Revert", loading: isPending }}
        cancelButton={{ label: "Cancel", disabled: isPending }}
      />

      <Show if={applying}>
        <ApplyUnitInstructionsModal
          unit={unit}
          initialType={type}
          onClose={() => setApplying(false)}
        />
      </Show>
    </SectionCard>
  );
};
